import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserRole } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { AlertV2, Button, Modal, Tapable, toast } from '@feathr/components';
import { EAlertType } from '@feathr/components/src/AlertV2/AlertV2';
import { useStore } from '@feathr/extender/state';
import { errorMessage, flattenError, generateKeys, logUserEvents } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import type { IUserInviteData } from './UserInviteRow';
import UserInviteRow from './UserInviteRow';

import * as styles from './InviteUsersModal.css';

interface IUserInviteWithId extends IUserInviteData {
  id: string;
  key?: string;
}

interface IBulkAssignModalProps {
  onClose: () => void;
}

const COLUMN_HEADER_IDS = {
  firstName: 'invite-users-header-first-name',
  lastName: 'invite-users-header-last-name',
  email: 'invite-users-header-email',
  role: 'invite-users-header-role',
} as const;

function InviteUsersModal({ onClose }: Readonly<IBulkAssignModalProps>): JSX.Element {
  const { Users, UserRoles } = useStore();
  const { t } = useTranslation();
  const nextIdRef = useRef(1);

  const [userInvites, setUserInvites] = useState<IUserInviteWithId[]>(() =>
    generateKeys([{ id: '0', firstName: '', lastName: '', email: '', role: undefined }]),
  );
  const [firstNameFocusStates, setFirstNameFocusStates] = useState<Record<string, boolean>>({
    '0': false,
  });
  const [lastNameFocusStates, setLastNameFocusStates] = useState<Record<string, boolean>>({
    '0': false,
  });
  const [emailFocusStates, setEmailFocusStates] = useState<Record<string, boolean>>({ '0': false });
  const [roleOptions, setRoleOptions] = useState<ISelectOption[]>([]);

  // Load Users (needed for duplicate email validation)
  useEffect(() => {
    async function loadUsers(): Promise<void> {
      try {
        const users = Users.list();
        await when(() => !users.isPending);
      } catch (error) {
        toast(t('Failed to load users: {{- error}}', { error: errorMessage(error, t) }), {
          type: 'error',
        });
      }
    }

    loadUsers();
  }, [Users, t]);

  // Load Roles
  useEffect(() => {
    async function loadRoles(): Promise<void> {
      try {
        const roles = UserRoles.list();
        await when(() => !roles.isPending);
        const options = roles.models.map(({ id, name }: UserRole): ISelectOption => ({ id, name }));
        setRoleOptions(options);
      } catch (error) {
        toast(t('Failed to load roles: {{- error}}', { error: errorMessage(error, t) }), {
          type: 'error',
        });
      }
    }

    loadRoles();
  }, [UserRoles, t]);

  function handleClose(): void {
    // Reset states on close
    nextIdRef.current = 1;
    setUserInvites(
      generateKeys([{ id: '0', firstName: '', lastName: '', email: '', role: undefined }]),
    );
    setFirstNameFocusStates({ '0': false });
    setLastNameFocusStates({ '0': false });
    setEmailFocusStates({ '0': false });
    onClose();
  }

  function handleAddAnother(): void {
    const newId = String(nextIdRef.current++);
    setUserInvites(
      generateKeys([
        ...userInvites,
        { id: newId, firstName: '', lastName: '', email: '', role: undefined },
      ]),
    );
    setFirstNameFocusStates({ ...firstNameFocusStates, [newId]: false });
    setLastNameFocusStates({ ...lastNameFocusStates, [newId]: false });
    setEmailFocusStates({ ...emailFocusStates, [newId]: false });
  }

  function handleUpdateUser(id: string, data: IUserInviteData): void {
    const updated = userInvites.map((invite) =>
      invite.id === id ? { ...invite, ...data } : invite,
    );
    setUserInvites(generateKeys(updated));
  }

  function handleRemoveUser(id: string): void {
    const updated = userInvites.filter((invite) => invite.id !== id);
    const { [id]: removedFirstName, ...updatedFirstNameFocus } = firstNameFocusStates;
    const { [id]: removedLastName, ...updatedLastNameFocus } = lastNameFocusStates;
    const { [id]: removedEmail, ...updatedEmailFocus } = emailFocusStates;
    setUserInvites(generateKeys(updated));
    setFirstNameFocusStates(updatedFirstNameFocus);
    setLastNameFocusStates(updatedLastNameFocus);
    setEmailFocusStates(updatedEmailFocus);
  }

  function handleFirstNameBlur(id: string): void {
    setFirstNameFocusStates({ ...firstNameFocusStates, [id]: true });
  }

  function handleLastNameBlur(id: string): void {
    setLastNameFocusStates({ ...lastNameFocusStates, [id]: true });
  }

  function handleEmailBlur(id: string): void {
    setEmailFocusStates({ ...emailFocusStates, [id]: true });
  }

  function validateEmail(invite: IUserInviteWithId): string[] {
    const errors: string[] = [];

    // email validation: check in order and stop at first error
    if (!invite.email || invite.email.trim() === '') {
      errors.push(t('Email address is required.'));
    } else {
      // Check for valid email format
      const emailFormatValidation = validate.single(invite.email, {
        email: { message: '^Enter a valid email address.' },
      });
      if (emailFormatValidation) {
        errors.push(...(flattenError(emailFormatValidation) ?? []));
      } else {
        // Check if user already exists in the system
        const existingUser = Users.list().models.find(
          (user) => user.email?.toLowerCase() === invite.email.toLowerCase(),
        );
        if (existingUser) {
          errors.push(t('This user already exists in your organization.'));
        } else {
          // Check for duplicate emails within the invite list
          const duplicateEmail = userInvites.find(
            (other) =>
              other.id !== invite.id && other.email.toLowerCase() === invite.email.toLowerCase(),
          );
          if (duplicateEmail) {
            errors.push(
              t('This email address has already been added. Use a different email address.'),
            );
          }
        }
      }
    }

    return errors;
  }

  async function handleInvite(): Promise<void> {
    try {
      const users = userInvites.map((invite) => ({
        first_name: invite.firstName,
        last_name: invite.lastName,
        email: invite.email,
        role: invite.role ?? '',
      }));

      // If there are more than 10 users to invite, the invites are processed as an async job.
      if (userInvites.length < 10) {
        await Users.invite({ users });
        toast(t('Sent invite links to {{count}} user', { count: userInvites.length }), {
          type: 'success',
        });
      } else {
        Users.invite({ users });
        toast(t('Your invites are being processed. Check back later.'), { type: 'info' });
      }

      logUserEvents({
        'Invited user': {
          invited_users_emails: users.map((u) => u.email),
        },
      });

      // Delay modal close to prevent ResizeObserver errors
      setTimeout(() => {
        handleClose();
      }, 100);
    } catch (error) {
      toast(t('There was an error sending your invites.\n{{- error}}', { error }), {
        type: 'error',
      });
    }
  }

  const validationErrors = useMemo(() => {
    const allErrors = userInvites.map((invite) => {
      const errors: string[] = [];

      // First name and last name are optional - no validation needed

      // Email validation
      errors.push(...validateEmail(invite));

      // Role validation
      const roleValidation = validate.single(invite.role, {
        presence: { allowEmpty: false, message: '^Role must be selected.' },
      });
      if (roleValidation) {
        errors.push(...(flattenError(roleValidation) ?? []));
      }

      return errors;
    });

    return allErrors.flat();
  }, [userInvites]);

  const hasExistingUserError = useMemo(() => {
    return userInvites.some((invite) => {
      const existingUser = Users.list().models.find(
        (user) => user.email?.toLowerCase() === invite.email.toLowerCase(),
      );
      return existingUser && invite.email.trim() !== '';
    });
  }, [userInvites, Users]);

  const confirmButton = (
    <Button
      aria-label={t('Send invitations to entered email addresses')}
      disabled={validationErrors.length > 0}
      onClick={handleInvite}
      tooltip={validationErrors.length > 0 ? validationErrors[0] : null}
      type={'primary'}
    >
      {t('Send invitations')}
    </Button>
  );

  const cancelButton = (
    <Button
      aria-label={t('Cancel inviting users and close modal')}
      onClick={onClose}
      type={'secondary'}
    >
      {t('Cancel')}
    </Button>
  );

  const userCount = (
    <div className={styles.userCount}>
      {userInvites.length === 1
        ? t('{{count}} user to invite', { count: userInvites.length })
        : t('{{count}} users to invite', { count: userInvites.length })}
    </div>
  );

  return (
    <Modal
      allowOverflow={true}
      description={t('Add team members by entering their details below.')}
      leftActions={userCount}
      onClose={onClose}
      opened={true}
      rightActions={
        <>
          {cancelButton} {confirmButton}
        </>
      }
      size={'lg'}
      title={t('Invite Users')}
      transitionProps={{ duration: 1000, transition: 'slide-up', timingFunction: 'ease-out' }}
    >
      <div className={styles.container}>
        {hasExistingUserError && (
          <AlertV2
            description={t(
              'Some email addresses are duplicates or already exist. Resolve the errors below.',
            )}
            title={t('Cannot send invitations')}
            type={EAlertType.danger}
          />
        )}
        <div className={styles.columnHeaders}>
          <div className={styles.headerLabel} id={COLUMN_HEADER_IDS.firstName}>
            {t('First name')}
          </div>
          <div className={styles.headerLabel} id={COLUMN_HEADER_IDS.lastName}>
            {t('Last name')}
          </div>
          <div className={styles.headerLabel} id={COLUMN_HEADER_IDS.email}>
            {t('Email address')}
          </div>
          <div className={styles.headerLabel} id={COLUMN_HEADER_IDS.role}>
            {t('Role')}
          </div>
          <div aria-hidden={'true'} className={styles.headerSpacer}></div>
        </div>
        <div className={styles.userInvites}>
          {userInvites.map((invite) => {
            // First name and last name are optional - no validation errors
            const firstNameErrors: string[] = [];
            const lastNameErrors: string[] = [];

            const emailErrors = validateEmail(invite);
            // needed to prevent ResizeObserver errors
            const rowKey = invite.key ?? invite.id;
            const fieldIdSuffix = `${rowKey}-${Math.random().toString(36).slice(2)}`;

            return (
              <UserInviteRow
                columnLabelIds={COLUMN_HEADER_IDS}
                data={invite}
                emailHadFocus={emailFocusStates[invite.id] ?? false}
                emailValidationErrors={emailErrors}
                fieldIdSuffix={fieldIdSuffix}
                firstNameHadFocus={firstNameFocusStates[invite.id] ?? false}
                firstNameValidationErrors={firstNameErrors}
                key={rowKey}
                lastNameHadFocus={lastNameFocusStates[invite.id] ?? false}
                lastNameValidationErrors={lastNameErrors}
                onEmailBlur={handleEmailBlur}
                onFirstNameBlur={handleFirstNameBlur}
                onLastNameBlur={handleLastNameBlur}
                onRemove={handleRemoveUser}
                onUpdate={handleUpdateUser}
                roleOptions={roleOptions}
                rowKey={rowKey}
                showRemove={userInvites.length > 1}
              />
            );
          })}
        </div>

        <div className={styles.addButton}>
          <Tapable>
            <Button
              aria-label={t('Add another user')}
              onClick={handleAddAnother}
              prefix={<FontAwesomeIcon icon={faPlus} />}
              type={'secondary'}
            >
              {t('Add another user')}
            </Button>
          </Tapable>
        </div>
      </div>
    </Modal>
  );
}

export default observer(InviteUsersModal);
